'use client';

import { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface OrderSummaryProps {
  price: number;
}

export default function OrderSummary({ price }: OrderSummaryProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const tax = 0; // No tax for digital products
  const total = price + tax;

  return (
    <div className="lg:sticky lg:top-32 bg-card border border-border rounded-2xl p-8 shadow-lg">
      {/* Book Cover */}
      <div className="aspect-[3/4] w-full max-w-xs mx-auto rounded-xl overflow-hidden shadow-xl mb-6">
        <AppImage
          src="/assets/images/Book_cover_rev_2-1771453636562.jpeg"
          alt="The Modern Job Playbook book cover for checkout summary"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Product Info */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-foreground mb-2">The Modern Job Playbook</h3>
        <p className="text-sm text-muted-foreground">Digital ebook (PDF format)</p>
      </div>

      {/* Price Breakdown */}
      <div className="space-y-3 mb-6 pb-6 border-b border-border">
        <div className="flex justify-between text-foreground">
          <span>Subtotal</span>
          <span className="font-semibold">${price.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-foreground">
          <span>Tax</span>
          <span className="font-semibold">${tax.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-lg font-bold text-foreground pt-3 border-t border-border">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
      </div>

      {/* What You Get */}
      <div className="mb-6">
        <h4 className="text-sm font-bold text-foreground mb-4 uppercase tracking-wider">
          What You Get:
        </h4>
        <ul className="space-y-3">
          <li className="flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
            <span className="text-sm text-foreground">Instant PDF download (93 pages)</span>
          </li>
          <li className="flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
            <span className="text-sm text-foreground">26 comprehensive chapters</span>
          </li>
          <li className="flex items-center gap-3">
            <Icon name="CheckCircleIcon" size={20} className="text-primary shrink-0" />
            <span className="text-sm text-foreground">Lifetime access to all updates</span>
          </li>
        </ul>
      </div>

      {/* Trust Badges */}
      <div className="space-y-4 mb-6 pb-6 border-b border-border">
        <div className="flex items-center gap-3 text-sm text-foreground">
          <Icon name="LockClosedIcon" size={20} className="text-primary" />
          <span>Secure SSL encrypted checkout</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-foreground">
          <Icon name="BoltIcon" size={20} className="text-primary" />
          <span>Instant delivery to your email</span>
        </div>
        <div className="flex items-center gap-3 text-sm text-foreground">
          <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
          <span>30-day money-back guarantee</span>
        </div>
      </div>
    </div>
  );
}